<template>
  <div class="app-nav-root">
    <header class="app-nav" :class="{ 'nav-scrolled': isScrolled }">
      <v-container>
        <div class="nav-content">
          <div
            class="nav-brand"
            role="link"
            tabindex="0"
            aria-label="Cleano Management home"
            @click="goHome"
            @keydown.enter="goHome"
          >
            <div class="nav-logo-icon">
              <svg class="brand-mark" viewBox="0 0 32 32" fill="none" aria-hidden="true">
                <path
                  d="M4 15 L16 5 L28 15 M7 12.5 V26 H25 V12.5"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  class="brand-spark"
                  d="M16 14.5 C16.6 17.2 17.8 18.4 20.5 19 C17.8 19.6 16.6 20.8 16 23.5 C15.4 20.8 14.2 19.6 11.5 19 C14.2 18.4 15.4 17.2 16 14.5 Z"
                />
              </svg>
            </div>
            <div class="nav-logo-text">
              <div class="nav-cleano">Cleano</div>
              <div class="nav-management">MANAGEMENT</div>
            </div>
          </div>

          <nav class="nav-links" aria-label="Main">
            <router-link to="/" class="nav-link" exact>Home</router-link>
            <router-link to="/why" class="nav-link">Why Cleano</router-link>
            <router-link to="/features" class="nav-link">Features</router-link>
            <router-link to="/about" class="nav-link">About</router-link>
            <router-link to="/tribzz" class="nav-link">Tribzz</router-link>
            <router-link to="/blog" class="nav-link">Blog</router-link>
          </nav>

          <div class="nav-actions">
            <v-btn outlined class="nav-btn-login" to="/login">
              Login
            </v-btn>
            <v-btn depressed class="nav-btn-demo" to="/demo">
              Request Demo
            </v-btn>
          </div>

          <button
            class="nav-burger"
            type="button"
            aria-label="Open menu"
            @click="drawer = true"
          >
            <v-icon color="white">mdi-menu</v-icon>
          </button>
        </div>
      </v-container>
    </header>

    <!-- Mobile Drawer: deliberately a SIBLING of <header>, not nested inside it.
         .app-nav uses backdrop-filter for its frosted-glass effect, and
         backdrop-filter creates a new containing block for any position:fixed
         descendants, so a drawer nested in <header> would be clipped to the
         header's height instead of the full viewport. -->
    <div v-if="drawer" class="drawer-overlay" @click="drawer = false">
      <div class="drawer-panel" role="dialog" aria-modal="true" aria-label="Menu" @click.stop>
        <div class="drawer-header">
          <div class="drawer-logo">
            <svg class="brand-mark brand-mark-drawer" viewBox="0 0 32 32" fill="none" aria-hidden="true">
              <path
                d="M4 15 L16 5 L28 15 M7 12.5 V26 H25 V12.5"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                class="brand-spark"
                d="M16 14.5 C16.6 17.2 17.8 18.4 20.5 19 C17.8 19.6 16.6 20.8 16 23.5 C15.4 20.8 14.2 19.6 11.5 19 C14.2 18.4 15.4 17.2 16 14.5 Z"
              />
            </svg>
          </div>
          <div class="drawer-brand">
            <div>Cleano</div>
            <div class="brand-sub">MANAGEMENT</div>
          </div>
          <button class="drawer-close" type="button" aria-label="Close menu" @click="drawer = false">
            <v-icon>mdi-close</v-icon>
          </button>
        </div>
        <v-divider class="my-4"></v-divider>
        <div class="drawer-nav">
          <a class="drawer-link" @click="navigateTo('/')">
            <v-icon left>mdi-home</v-icon> Home
          </a>
          <a class="drawer-link" @click="navigateTo('/why')">
            <v-icon left>mdi-star-four-points</v-icon> Why Cleano
          </a>
          <a class="drawer-link" @click="navigateTo('/features')">
            <v-icon left>mdi-view-grid</v-icon> Features
          </a>
          <a class="drawer-link" @click="navigateTo('/about')">
            <v-icon left>mdi-information</v-icon> About
          </a>
          <a class="drawer-link" @click="navigateTo('/tribzz')">
            <v-icon left>mdi-account-group-outline</v-icon> Tribzz
          </a>
          <a class="drawer-link" @click="navigateTo('/blog')">
            <v-icon left>mdi-post</v-icon> Blog
          </a>
          <v-divider class="my-4"></v-divider>
          <a class="drawer-link" @click="navigateTo('/login')">
            <v-icon left>mdi-login</v-icon> Login
          </a>
          <a class="drawer-link drawer-link-accent" @click="navigateTo('/demo')">
            <v-icon left>mdi-calendar-check</v-icon> Request Demo
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AppNavigation',
  data() {
    return {
      drawer: false,
      isScrolled: false
    };
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('keydown', this.handleKeydown);
    this.handleScroll();
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('keydown', this.handleKeydown);
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 20;
    },
    handleKeydown(e) {
      if (e.key === 'Escape') this.drawer = false;
    },
    // Pushes a route without throwing when the user is already on it.
    push(route) {
      const result = this.$router.push(route);
      if (result && typeof result.catch === 'function') result.catch(() => {});
    },
    navigateTo(route) {
      this.drawer = false;
      this.push(route);
    },
    goHome() {
      if (this.$route && this.$route.path === '/') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        this.push('/');
      }
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&display=swap');

.app-nav-root {
  display: contents;
}

.app-nav {
  --border-subtle: rgba(255, 255, 255, 0.09);
  --text-primary: #EEF3F8;
  --text-secondary: rgba(238, 243, 248, 0.68);
  --accent: #5BB4E5;
  --accent-light: #A6DBF7;
  --accent-dark: #3A8CC2;

  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 999;
  background: rgba(7, 10, 16, 0.88);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--border-subtle);
  font-family: 'Syne', -apple-system, BlinkMacSystemFont, sans-serif;
  transition: background 0.3s ease, box-shadow 0.3s ease;
}

.app-nav.nav-scrolled {
  background: rgba(5, 7, 12, 0.96);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
}

.app-nav ::v-deep .v-btn {
  text-transform: none !important;
}

.nav-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 0;
}

.nav-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.nav-brand:hover {
  transform: scale(1.03);
}

.nav-brand:focus-visible,
.nav-link:focus-visible,
.nav-burger:focus-visible,
.drawer-close:focus-visible,
.drawer-link:focus-visible {
  outline: 2px solid var(--accent-light);
  outline-offset: 3px;
}

.nav-logo-icon {
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.brand-mark {
  width: 32px;
  height: 32px;
  color: var(--accent-light);
}

.brand-spark {
  fill: var(--accent);
}

.nav-logo-text {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.nav-cleano {
  font-family: 'Syne', sans-serif;
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--text-primary);
  letter-spacing: -0.01em;
}

.nav-management {
  font-size: 0.62rem;
  font-weight: 600;
  letter-spacing: 2.5px;
  color: var(--accent);
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 6px;
}

.nav-link {
  padding: 10px 16px;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  border-radius: 8px;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.nav-link:hover,
.nav-link.router-link-active {
  color: var(--accent-light);
  background: rgba(91, 180, 229, 0.1);
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.nav-btn-login {
  border: 1.5px solid var(--accent) !important;
  color: var(--accent-light) !important;
  font-weight: 600 !important;
  border-radius: 9px !important;
}

.nav-btn-demo {
  background: linear-gradient(150deg, var(--accent-light) 0%, var(--accent) 60%, var(--accent-dark) 100%) !important;
  color: #06121C !important;
  font-weight: 700 !important;
  border-radius: 9px !important;
}

.nav-burger {
  display: none;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  cursor: pointer;
}

/* ==================== MOBILE DRAWER ==================== */
.drawer-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
  z-index: 1000;
}

.drawer-panel {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: 300px;
  max-width: 88vw;
  background: #0A0E15;
  border-right: 1px solid rgba(255, 255, 255, 0.09);
  box-shadow: 0 0 60px rgba(0, 0, 0, 0.5);
  z-index: 1001;
  overflow-y: auto;
  padding: 8px;
  font-family: 'Syne', sans-serif;
}

.drawer-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 24px 16px 0;
}

.drawer-logo {
  width: 46px;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.brand-mark-drawer {
  width: 34px;
  height: 34px;
  color: #A6DBF7;
}

.brand-mark-drawer .brand-spark {
  fill: #5BB4E5;
}

.drawer-brand {
  flex: 1;
  line-height: 1.2;
}

.drawer-brand > div:first-child {
  font-size: 18px;
  font-weight: 700;
  color: #EEF3F8;
}

.brand-sub {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 2px;
  color: #5BB4E5;
}

.drawer-close {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 8px;
  color: rgba(238, 243, 248, 0.68);
  cursor: pointer;
}

.drawer-close ::v-deep .v-icon {
  color: currentColor !important;
}

.drawer-nav {
  padding: 8px 16px 16px;
}

.drawer-link {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  color: rgba(238, 243, 248, 0.68);
  font-size: 16px;
  font-weight: 600;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 4px;
}

.drawer-link ::v-deep .v-icon {
  color: currentColor !important;
}

.drawer-link:hover {
  background: rgba(91, 180, 229, 0.12);
  color: #A6DBF7;
  transform: translateX(8px);
}

.drawer-link-accent {
  color: #A6DBF7;
}

@media (max-width: 960px) {
  .nav-links,
  .nav-actions {
    display: none;
  }

  .nav-burger {
    display: flex;
  }
}

@media (prefers-reduced-motion: reduce) {
  .nav-brand,
  .drawer-link,
  .app-nav {
    transition: none;
  }

  .nav-brand:hover,
  .drawer-link:hover {
    transform: none;
  }
}
</style>